'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { FileText, Eye } from 'lucide-react';

interface Catalog {
  id: string;
  title: string;
  filename: string;
  category: string;
  description: string;
  thumbnail?: string;
}

const CatalogsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const router = useRouter();

  const catalogs: Catalog[] = [
    {
      id: '1',
      title: 'Arisca Acrylic Collection',
      filename: '04 - Arisca_acrylic folder.pdf',
      category: 'Acrylic',
      description: 'Premium acrylic surfaces and finishes for modern interiors'
    },
    {
      id: '2',
      title: 'Altis Collection V3',
      filename: 'F_ALTIS_V3.pdf',
      category: 'Laminates',
      description: 'Contemporary laminate designs with superior durability'
    },
    {
      id: '3',
      title: 'Artiq Collection',
      filename: 'F_ARTIQ.pdf',
      category: 'Laminates',
      description: 'Artistic laminate patterns for unique interior expressions'
    },
    {
      id: '4',
      title: 'Grande Collection',
      filename: 'Grande-Collection.pdf',
      category: 'Premium',
      description: 'Luxurious finishes for high-end residential and commercial spaces'
    },
    {
      id: '5',
      title: 'Kridha 0.8mm Folder 2025',
      filename: 'Kridha 0.8mm Folder 2025.pdf',
      category: 'Laminates',
      description: 'Ultra-thin laminates with exceptional flexibility and finish'
    },
    {
      id: '6',
      title: 'Luvih E-Catalogue 2025',
      filename: 'Luvih-E-Catalogue-2025.pdf',
      category: 'Special Collection',
      description: 'Comprehensive collection of contemporary interior solutions'
    },
    {
      id: '7',
      title: 'Merino Laminates Play 2025',
      filename: 'Merino-Laminates-Play-E-Catalogue-2025.pdf',
      category: 'Laminates',
      description: 'Playful and vibrant laminate designs for creative spaces'
    },
    {
      id: '8',
      title: 'Satvario by Kridha 2026',
      filename: 'Satvario by kridha 2026.pdf',
      category: 'Premium',
      description: 'Latest premium collection with innovative designs and textures'
    }
  ];

  const categories = ['all', ...Array.from(new Set(catalogs.map(c => c.category)))];

  const filteredCatalogs = selectedCategory === 'all'
    ? catalogs
    : catalogs.filter(c => c.category === selectedCategory);

  // Keep the three specified catalogs (Luvih, Merino, Grande) at the top in the requested order
  const priorityOrder = [
    'Luvih-E-Catalogue-2025.pdf',
    'Merino-Laminates-Play-E-Catalogue-2025.pdf',
    'Grande-Collection.pdf'
  ];
  const brandFilenames = new Set(priorityOrder);

  const orderedCatalogs = [
    // Place the prioritized catalogs first, in the exact order requested
    ...priorityOrder
      .map(fn => filteredCatalogs.find(c => c.filename === fn))
      .filter(Boolean) as Catalog[],
    // Then the rest
    ...filteredCatalogs.filter(c => !brandFilenames.has(c.filename)),
  ];


  const handleView = (filename: string) => {
    window.open(`/catlogs/${filename}`, '_blank');
  };

  return (
    <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-gradient-to-b from-white to-zinc-50">
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-6">
            <div className="w-1.5 h-1.5 bg-black rounded-full" />
            <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
              Product Catalogs
            </span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-black mb-6 tracking-tight">
            Our Material Catalogs
          </h2>
          <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal">
            Browse our curated collection of premium materials, finishes, and surfaces to bring your vision to life
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 md:mb-10 lg:mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 border ${
                selectedCategory === category
                  ? 'bg-[#1B5E4F] text-white border-[#1B5E4F] shadow-lg scale-105'
                  : 'bg-white text-black border-zinc-200 hover:border-[#1B5E4F] hover:text-[#1B5E4F]'
              }`}
            >
              {category === 'all' ? 'All Catalogs' : category}
            </button>
          ))}
        </div>

        {/* Catalogs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {orderedCatalogs.map((catalog) => (
            <div
              key={catalog.id}
              className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
            >
              {/* Card Header */}
              <div className="relative h-48 bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />

                {/* Attempt to embed the PDF first page as a preview. Browser will render the PDF first page inside object/iframe in most modern browsers. */}
                <div className="w-full h-full flex items-center justify-center">
                  <object
                    data={`/catlogs/${catalog.filename}#page=1`}
                    type="application/pdf"
                    className="w-full h-full"
                    aria-label={`${catalog.title} preview`}
                  >
                    {/* Fallback: show the file icon if embedding is not supported */}
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-20 h-20 text-zinc-400" />
                    </div>
                  </object>
                </div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-medium text-black/80 uppercase tracking-wider">
                    {catalog.category}
                  </span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <h3 className="font-headline text-xl font-medium text-black mb-2 line-clamp-2 group-hover:text-black/80 transition-colors">
                  {catalog.title}
                </h3>
                <p className="text-sm text-black/60 mb-6 line-clamp-2">
                  {catalog.description}
                </p>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleView(catalog.filename)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-zinc-50 hover:bg-zinc-100 text-black rounded-xl transition-all duration-300 border border-zinc-200 hover:border-zinc-300 active:scale-95 min-h-[44px]"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">View</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-16 p-8 bg-white rounded-2xl border border-zinc-200 shadow-sm">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-headline text-2xl font-normal text-black mb-4">
              Need Help Choosing Materials?
            </h3>
            <p className="text-black/60 mb-6">
              Our design experts can guide you through material selection based on your project requirements, budget, and aesthetic preferences.
            </p>
            <button onClick={() => router.push('/contact')} className="px-8 py-4 bg-black text-white font-medium rounded-full hover:bg-black/90 transition-all duration-300 hover:shadow-lg hover:scale-105">
              Schedule a Material Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CatalogsSection;
